import React, { useState, useEffect } from 'react';
import { configureAmplify } from './graphql/config';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, toast as hotToast, ToastBar } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { ApolloProvider } from '@apollo/client';
import apolloClient from './graphql/apollo-client';
import { I18nextProvider } from 'react-i18next';
import { initializeI18n } from './i18n/config';
import { usePermissions } from './hooks/usePermissions';
import { LoginForm } from './components/auth/LoginForm';
import { RegisterForm } from './components/auth/RegisterForm';
import { ConfirmEmailForm } from './components/auth/ConfirmEmailForm';
import { ForgotPasswordForm } from './components/auth/ForgotPasswordForm';
import { ResetPasswordForm } from './components/auth/ResetPasswordForm';
import { InstagramCallback } from './components/auth/InstagramCallback';
import { OAuthCallbackHandler } from './components/auth/OAuthCallbackHandler';
import { CompleteOAuthRegistration } from './components/auth/CompleteOAuthRegistration';
import { Dashboard } from './components/dashboard/Dashboard';
import { InfluencerSearch } from './components/search/InfluencerSearch';
import { Layout } from './components/layout/Layout';
import { CampaignsList } from './components/campaigns/CampaignsList';
import { CampaignDiscovery } from './components/campaigns/CampaignDiscovery';
import { CampaignDetailPage } from './components/campaigns/CampaignDetailPage';
import CampaignRegistrationForm from './components/campaigns/CampaignRegistrationForm';
import Messages from './components/messages/Messages';
import { MatchesPage } from './components/matches/MatchesPage';
import { PublicDashboard } from './components/public/PublicDashboard';
import PublicDashboardV2 from './components/public/PublicDashboardV2';
import MainPage from './components/public/MainPage';
import { PublicCampaignDetails } from './components/public/PublicCampaignDetails';
import { PublicInfluencerProfile } from './components/public/PublicInfluencerProfile';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminLayout from './components/admin/AdminLayout';
import UserManagementPage from './components/admin/UserManagementPage';
import CampaignManagementPage from './components/admin/CampaignManagementPage';
import UserRolesPage from './components/admin/UserRolesPage';
import DocumentTemplateListPage from './components/admin/DocumentTemplateListPage';
import DocumentTemplateEditorPage from './components/admin/DocumentTemplateEditorPage';
import AdminInquiryListPage from './components/admin/AdminInquiryListPage';
import { TermsOfServicePage } from './pages/legal/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/legal/PrivacyPolicyPage';
import MarketingConsentCollectionPage from './pages/legal/MarketingConsentCollectionPage';
import MarketingConsentPromotionPage from './pages/legal/MarketingConsentPromotionPage';

import { ProfileExistenceDebug } from './components/debug/ProfileExistenceDebug';
import MyProfilePage from './components/profile/influencer/MyProfilePage';
import MyInformationPage from './components/profile/influencer/MyInformationPage';
import ProfileSubPage from './components/profile/influencer/ProfileSubPage';
import MyCampaignsPage from './components/profile/influencer/MyCampaignsPage';
import CollaborationAcceptPage from './components/profile/influencer/CollaborationAcceptPage';
import SettlementPage from './components/profile/influencer/SettlementPage';
import ContentSubmitPage from './components/profile/influencer/ContentSubmitPage';
import SocialConnectCallback from './components/profile/SocialConnectCallback';
import HostMyInformationPage from './components/profile/host/HostMyInformationPage';
import HostBusinessProfilePage from './components/profile/host/HostBusinessProfilePage';
import ClientBrandsPage from './components/profile/host/ClientBrandsPage';
import PlaceholderPage from './components/profile/PlaceholderPage';
import NoticePage from './components/notice/NoticePage';
import NoticeDetailPage from './components/notice/NoticeDetailPage';
import InquiryListPage from './components/inquiry/InquiryListPage';
import InquiryDetailPage from './components/inquiry/InquiryDetailPage';
import InquiryCreatePage from './components/inquiry/InquiryCreatePage';
import CampaignApplicantsPage from './components/campaigns/CampaignApplicantsPage';
import NoPermission from './components/common/NoPermission';
import { getLastVisitedPath, recordLastVisitedPath, getPostAuthRedirectPath } from './utils/authRedirect';

/**
 * Records every route change into sessionStorage so the login page can
 * redirect back to the last page the user was actually viewing. We do this
 * instead of relying on `<Link to="/login" state={{from}}>` because the
 * state gets lost across OAuth round-trips, hard reloads, direct /login
 * URL visits, and any nav-bar link that didn't pass state. Placing this
 * inside <Router> ensures `useLocation` is available.
 */
const LocationTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const full = `${location.pathname}${location.search}${location.hash}`;
    // Debug trace — remove once post-login redirect is verified.
    // eslint-disable-next-line no-console
    console.log('[LocationTracker] recording', full);
    recordLastVisitedPath(full);
  }, [location.pathname, location.search, location.hash]);
  return null;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (isAuthenticated) {
    const last = getLastVisitedPath();
    // Debug trace — remove once post-login redirect is verified.
    // eslint-disable-next-line no-console
    console.log('[PublicRoute] authenticated, redirect target', {
      last,
      storedIntent: sessionStorage.getItem('auth_redirect_path'),
    });
    if (last) return <Navigate to={last} replace />;
    const stored = getPostAuthRedirectPath('/dashboard');
    return <Navigate to={stored} replace />;
  }
  return <>{children}</>;
};

const PublicAccessRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Public access routes are accessible to everyone (authenticated or not)
  return <>{children}</>;
};

const PermissionBasedRoute: React.FC<{ 
  children: React.ReactNode;
  permission: string;
  fallbackPath?: string;
}> = ({ children, permission, fallbackPath = '/dashboard' }) => {
  const { isAuthenticated } = useAuthStore();
  const { hasPermission, loading } = usePermissions();
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  if (!hasPermission(permission)) {
    return <Layout><NoPermission /></Layout>;
  }
  
  return <>{children}</>;
};

const HostOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  const { hasPermission, loading } = usePermissions();
  
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }
  
  // Allow if user has RBAC permission OR is a host-like userType (brand/agency)
  const userType = user?.userType;
  const isHostLikeUser = userType === 'brand' || userType === 'agency' || userType === 'host';
  
  if (!hasPermission('campaigns:create') && !isHostLikeUser) {
    return <Layout><NoPermission /></Layout>;
  }
  
  return <>{children}</>;
};

const InfluencerOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  const { hasRole, loading } = usePermissions();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const isInfluencer = user?.userType === 'influencer' || hasRole('influencer');
  if (!isInfluencer) {
    return <Layout><NoPermission /></Layout>;
  }

  return <>{children}</>;
};

function App() {
  const { checkAuthState } = useAuthStore();
  const [i18nInitialized, setI18nInitialized] = useState(false);

  useEffect(() => {
    // Configure Amplify
    configureAmplify();

    // Initialize i18n
    const initI18n = async () => {
      try {
        await initializeI18n();
        setI18nInitialized(true);
        console.log('✅ i18n initialized successfully');
      } catch (error) {
        console.error('❌ Failed to initialize i18n:', error);
        // Set initialized anyway to prevent blocking the app
        setI18nInitialized(true);
      }
    };

    // Check if user is already authenticated
    const initializeAuth = async () => {
      console.log('🚀 Initializing authentication...');
      await checkAuthState();
    };

    initI18n();
    initializeAuth();
  }, [checkAuthState]);

  // Show loading state while i18n is initializing
  if (!i18nInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ApolloProvider client={apolloClient}>
      <Router>
      <LocationTracker />
      <div className="App">
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              cursor: 'pointer',
            },
          }}
        >
          {/*
            Default react-hot-toast toasts stay pinned for `duration` ms and
            block clicks on whatever sits beneath them (e.g. the global nav
            menu in the top-right). Wrap each toast in a ToastBar whose
            outer container dismisses on click so users can clear a message
            that's in the way without waiting for it to time out.
          */}
          {(t) => (
            <div
              onClick={() => hotToast.dismiss(t.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') hotToast.dismiss(t.id);
              }}
              style={{ cursor: 'pointer' }}
              aria-label="Dismiss"
            >
              <ToastBar toast={t} />
            </div>
          )}
        </Toaster>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginForm />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterForm />
              </PublicRoute>
            }
          />
          <Route
            path="/confirm-email"
            element={
              <PublicRoute>
                <ConfirmEmailForm />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordForm />
              </PublicRoute>
            }
          />

          {/* Instagram OAuth Callback */}
          <Route
            path="/auth/facebook/callback"
            element={
              <InstagramCallback />
            }
          />
          {/* Legacy Instagram callback redirect */}
          <Route
            path="/auth/instagram/callback"
            element={
              <InstagramCallback />
            }
          />

          {/* OAuth Callback Route */}
          <Route
            path="/oauth/callback"
            element={
              <OAuthCallbackHandler />
            }
          />

          {/* Social Connect Callback (popup) */}
          <Route
            path="/social-connect/callback"
            element={<SocialConnectCallback />}
          />

          {/* Complete OAuth Registration - new users pick userType */}
          <Route
            path="/complete-registration"
            element={
              <CompleteOAuthRegistration />
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <HostOnlyRoute>
                <Layout>
                  <InfluencerSearch />
                </Layout>
              </HostOnlyRoute>
            }
          />

          {/* Host My Information */}
          <Route
            path="/host/info"
            element={
              <ProtectedRoute>
                <Layout>
                  <HostMyInformationPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Host Business Profile */}
          <Route
            path="/host/business-profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <HostBusinessProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Client Brands (Agency only) */}
          <Route
            path="/host/client-brands"
            element={
              <ProtectedRoute>
                <Layout>
                  <ClientBrandsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Payment Info */}
          <Route
            path="/host/payment"
            element={
              <ProtectedRoute>
                <Layout>
                  <PlaceholderPage titleKey="myProfile.paymentPage.title" messageKey="myProfile.paymentPage.description" />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* My Profile Routes (Influencer Only) */}
          <Route
            path="/my-profile"
            element={
              <InfluencerOnlyRoute>
                <Layout>
                  <MyProfilePage />
                </Layout>
              </InfluencerOnlyRoute>
            }
          >
            <Route index element={<Navigate to="/my-profile/info" replace />} />
            <Route path="info" element={<MyInformationPage />} />
            <Route path="profile" element={<ProfileSubPage />} />
            <Route path="campaigns" element={<MyCampaignsPage />} />
            <Route path="campaigns/accept/:matchId" element={<CollaborationAcceptPage />} />
            <Route path="campaigns/submit/:matchId" element={<ContentSubmitPage />} />
            <Route path="settlement" element={<SettlementPage />} />
            <Route path="notice" element={<PlaceholderPage titleKey="myProfile.sidebar.notice" messageKey="myProfile.placeholder.comingSoon" />} />
            <Route path="inquiry" element={<PlaceholderPage titleKey="myProfile.sidebar.inquiry" messageKey="myProfile.placeholder.comingSoon" />} />
          </Route>
          {/* Campaign Routes */}
          <Route
            path="/discover"
            element={
              <InfluencerOnlyRoute>
                <Layout>
                  <CampaignDiscovery />
                </Layout>
              </InfluencerOnlyRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <Layout>
                  <CampaignsList />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Legacy route — redirect to canonical /campaigns/register */}
          <Route
            path="/campaigns/create"
            element={<Navigate to="/campaigns/register" replace />}
          />
          <Route
            path="/campaigns/register"
            element={
              <HostOnlyRoute>
                <Layout>
                  <CampaignRegistrationForm />
                </Layout>
              </HostOnlyRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId/detail"
            element={
              <ProtectedRoute>
                <Layout>
                  <CampaignDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId/edit"
            element={
              <HostOnlyRoute>
                <Layout>
                  <CampaignRegistrationForm />
                </Layout>
              </HostOnlyRoute>
            }
          />
          <Route
            path="/campaigns/:campaignId/applicants"
            element={
              <HostOnlyRoute>
                <Layout>
                  <CampaignApplicantsPage />
                </Layout>
              </HostOnlyRoute>
            }
          />

          {/* Matches/Applications Route */}
          <Route
            path="/matches"
            element={
              <ProtectedRoute>
                <Layout>
                  <MatchesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Messages Route - Phase 3 */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Layout>
                  <Messages />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes - RBAC System */}
          <Route
            path="/admin"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <AdminDashboard />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/user-roles"
            element={
              <PermissionBasedRoute permission="roles:associate">
                <AdminLayout>
                  <UserRolesPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <UserManagementPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <CampaignManagementPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/campaign-managers"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <PlaceholderPage titleKey="admin:sidebar.campaignManagers" />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/document-templates"
            element={
              <PermissionBasedRoute permission="document_templates:manage">
                <AdminLayout>
                  <DocumentTemplateListPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/document-templates/:docType"
            element={
              <PermissionBasedRoute permission="document_templates:manage">
                <AdminLayout>
                  <DocumentTemplateEditorPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/notices"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <PlaceholderPage titleKey="admin:sidebar.notices" />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/inquiries"
            element={
              <PermissionBasedRoute permission="inquiries:manage">
                <AdminLayout>
                  <AdminInquiryListPage />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <PermissionBasedRoute permission="roles:read">
                <AdminLayout>
                  <PlaceholderPage titleKey="admin:sidebar.settings" />
                </AdminLayout>
              </PermissionBasedRoute>
            }
          />

          {/* Debug Route */}
          <Route
            path="/debug-profile"
            element={
              <ProtectedRoute>
                <Layout><ProfileExistenceDebug /></Layout>
              </ProtectedRoute>
            }
          />

          {/* Main page — Marketplace style (V2) */}
          <Route 
            path="/discovery" 
            element={
              <ErrorBoundary>
                <PublicDashboardV2 />
              </ErrorBoundary>
            } 
          />
          {/* Campaign and influencer detail routes are accessible to everyone */}
          <Route 
            path="/campaigns/:campaignId" 
            element={
              <PublicAccessRoute>
                <ErrorBoundary>
                  <PublicCampaignDetails />
                </ErrorBoundary>
              </PublicAccessRoute>
            } 
          />
          <Route 
            path="/influencers/:influencerId" 
            element={
              <PublicAccessRoute>
                <ErrorBoundary>
                  <PublicInfluencerProfile />
                </ErrorBoundary>
              </PublicAccessRoute>
            } 
          />

          {/* Notice Pages */}
          <Route path="/notice" element={<ProtectedRoute><Layout><NoticePage /></Layout></ProtectedRoute>} />
          <Route path="/notice/:noticeId" element={<ProtectedRoute><Layout><NoticeDetailPage /></Layout></ProtectedRoute>} />

          {/* Inquiry Pages (1:1문의) */}
          <Route path="/inquiry" element={<ProtectedRoute><Layout><InquiryListPage /></Layout></ProtectedRoute>} />
          <Route path="/inquiry/new" element={<ProtectedRoute><Layout><InquiryCreatePage /></Layout></ProtectedRoute>} />
          <Route path="/inquiry/:inquiryId" element={<ProtectedRoute><Layout><InquiryDetailPage /></Layout></ProtectedRoute>} />


          {/* Legal Pages */}
          <Route 
            path="/legal/terms-of-service" 
            element={
              <PublicAccessRoute>
                <TermsOfServicePage />
              </PublicAccessRoute>
            } 
          />
          <Route 
            path="/legal/privacy-policy" 
            element={
              <PublicAccessRoute>
                <PrivacyPolicyPage />
              </PublicAccessRoute>
            } 
          />
          <Route 
            path="/legal/marketing-consent-collection" 
            element={
              <PublicAccessRoute>
                <MarketingConsentCollectionPage />
              </PublicAccessRoute>
            } 
          />
          <Route 
            path="/legal/marketing-consent-promotion" 
            element={
              <PublicAccessRoute>
                <MarketingConsentPromotionPage />
              </PublicAccessRoute>
            } 
          />

          {/* Main landing page */}
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <MainPage />
              </ErrorBoundary>
            }
          />

          {/* Fallback redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      </Router>
    </ApolloProvider>
  );
}

export default App;
